import WhatsappIcon from '@mui/icons-material/WhatsApp';
import { Alert, Button, Grid, SxProps } from '@mui/material';
import { FC } from 'react';
import { Page } from '~/components/ui/page';
import { Body1, Title } from '~/components/ui/typography';
import { colors } from '~/theme';

/**
 * Toned down from the solid blue the theme gives every info alert, so that it
 * reads as a note instead of competing with the buttons below it.
 */
const InfoAlertSx: SxProps = {
  'mt': 2,
  'textAlign': 'left',
  'backgroundColor': colors.paleBlue,
  'color': colors.purple,
  // the icon is pinned to the first line by default, which looks off centered
  // against a message that wraps into more than one line
  'alignItems': 'center',
  '& .MuiAlert-icon': { color: colors.purple, py: 0 },
};

interface IdentityCheckSuccessScreenProps {
  whatsappNumber: string | null;
  /**
   * Whether the customer asked to talk with the attendant. Only then is there a
   * conversation waiting for them, so the way back to WhatsApp is offered and
   * mentioned; otherwise the validation is all there was to do here.
   */
  wantsSupport: boolean;
  /**
   * Whether any development the customer just opted in for the notifications of
   * has no issued contract they are the main customer of. Only the main customer
   * of a contract receives its boletos, so it is said upfront that not every
   * development just activated will send them.
   */
  hasOptedInWithoutBeingMainCustomer?: boolean;
  /**
   * When provided, the customer may go through the flow again to opt in for the
   * notifications of their other developments. Only provided when there is any
   * development left to opt in.
   */
  onOptInOtherDevelopments?: () => void;
}

export const IdentityCheckSuccessScreen: FC<IdentityCheckSuccessScreenProps> = ({
  whatsappNumber,
  wantsSupport,
  hasOptedInWithoutBeingMainCustomer = false,
  onOptInOtherDevelopments,
}) => {
  const handleWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}`, '_blank');
  };

  return (
    <Page innerWrapperSx={{ width: { xs: '100%', sm: '800px' } }}>
      <Title>Identidade confirmada!</Title>
      <img style={{ marginTop: '30px' }} src="/identity-confirmed.svg" />
      <Body1>
        {wantsSupport
          ? 'Tudo certo! Confirmamos que é você. Agora é só voltar para a conversa no WhatsApp e continuar de onde parou.'
          : 'Tudo certo! Confirmamos que é você'}
      </Body1>
      {onOptInOtherDevelopments && (
        <Body1>Você também pode ativar o envio dos boletos dos seus outros empreendimentos por aqui.</Body1>
      )}
      {hasOptedInWithoutBeingMainCustomer && (
        <Alert severity="info" sx={InfoAlertSx}>
          Importante: você recebe boletos apenas dos contratos em que é o titular
        </Alert>
      )}
      <Grid container spacing={2} justifyContent="center">
        {wantsSupport && whatsappNumber && (
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<WhatsappIcon />}
              onClick={handleWhatsApp}
              sx={{ fontWeight: 'bold', textTransform: 'none' }}
            >
              Voltar para WhatsApp
            </Button>
          </Grid>
        )}
        {onOptInOtherDevelopments && (
          <Grid item xs={12} sm={6}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onOptInOtherDevelopments}
              sx={{ fontWeight: 'bold', textTransform: 'none' }}
            >
              Ativar em outros
            </Button>
          </Grid>
        )}
      </Grid>
    </Page>
  );
};
